import styled from 'styled-components';
import Text from '~/components/Text';
import Logo from '~/components/Logo';
import ResponsiveText from '~/components/ResponsiveText';

const smallTableQuery = `@media (max-width: 1080px)`;

const mobileQuery = `@media (max-width: 500px)`;

export const Container = styled.div`
	display: flex;
	padding: 80px 120px 0;
	flex-direction: column;
	align-items: center;
	gap: 40px;

	${smallTableQuery} {
		padding: 40px 20px 0;
	}
`;

export const TableHeaderContainer = styled.div`
	display: flex;
	max-width: 620px;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 24px;
`;

export const Title = styled(ResponsiveText)`
	text-align: center;
`;

export const Subtitle = styled(ResponsiveText)`
	justify-self: center;
	text-align: center;
	margin: 0 100px;
	max-width: 800px;

	${mobileQuery} {
		margin: 0 50px;
	}
`;

export const TableContainer = styled.div`
	display: flex;
	max-width: 1318px;
	width: 100%;
	flex-direction: column;
	align-items: flex-end;
	border-radius: 12px;
	box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.04);
	overflow-x: auto;

	${smallTableQuery} {
		max-width: 600px;
	}
`;

export const Columns = styled.div`
	display: flex;
	align-items: flex-start;
	align-self: stretch;
	position: relative;
	min-width: 800px;

	${smallTableQuery} {
		min-width: 270px;
	}
`;

export const HeaderBackground = styled.div`
	width: 100%;
	height: 70px;
	position: absolute;
	background: ${({ theme }) =>
		theme.tokens.COLOR_TOKENS_SURFACE_TRANSPARENT_NEUTRAL_ULTRA_SUBTLE};
	z-index: -1;
	border-radius: 12px 12px 0 0;

	${smallTableQuery} {
		height: 50px;
	}
`;

export const FeaturesColumn = styled.div`
	display: flex;
	flex: 0 0 40%;
	min-width: 288px;
	flex-direction: column;
	align-items: flex-start;

	${smallTableQuery} {
		min-width: 90px;
		flex: 1 1 auto;
	}
`;

export const FeatureText = styled(Text)`
	color: ${({ theme }) =>
		theme.tokens.COLOR_TOKENS_TEXT_INVERTIBLE_NEUTRAL_NEUTRAL_STRONG};
	font-size: 20px;
	font-weight: 600;
	line-height: 25px;
	letter-spacing: 0;
	-webkit-line-clamp: 2;
	overflow: hidden;
	text-overflow: ellipsis;

	${smallTableQuery} {
		font-size: 14px;
		line-height: 18px;
	}

	${mobileQuery} {
		font-size: 11px;
		line-height: 14px;
		-webkit-line-clamp: 3;
	}
`;

export const StreamYardColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	flex: 1 0 0;
	min-width: 240px;
	border-radius: 20px;
	outline: 3px solid
		${({ theme }) =>
			theme.tokens.COLOR_TOKENS_FILL_INVERTIBLE_PRIMARY_PRIMARY_MODERATE};
	outline-offset: -3px;

	${smallTableQuery} {
		min-width: 90px;
		width: 90px;
		flex: 0 0 90px;
	}
`;

export const CompetitorColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	flex: 1 0 0;
	min-width: 240px;

	${smallTableQuery} {
		min-width: 90px;
		width: 90px;
		flex: 0 0 90px;
	}
`;

export const StyledText = styled(Text)`
	color: ${({ theme }) =>
		theme.tokens.COLOR_TOKENS_TEXT_INVERTIBLE_NEUTRAL_NEUTRAL_STRONG};
	font-size: 20px;
	font-weight: 400;
	line-height: 25px;
	letter-spacing: 0;

	${smallTableQuery} {
		display: none;
	}
`;

export const TableHeader = styled.div<{ isHighlighted?: boolean }>`
	display: flex;
	height: 70px;
	min-width: 240px;
	padding: 20px 32px;
	align-items: center;
	align-self: stretch;
	border-radius: 20px 20px 0 0;
	background: ${({ isHighlighted, theme }) =>
		isHighlighted
			? theme.tokens.COLOR_TOKENS_FILL_INVERTIBLE_PRIMARY_PRIMARY_MODERATE
			: 'none'};

	${smallTableQuery} {
		min-width: 90px;
		padding: 15px 16px;
		height: 50px;
		justify-content: center;
	}
`;

export const TableCell = styled.div<{
	isLast?: boolean;
	isFeatureCell?: boolean;
}>`
	display: flex;
	height: 84px;
	min-width: 85px;
	padding: 8px 32px;
	align-items: center;
	gap: 16px;
	align-self: stretch;
	border-bottom: ${({ isLast, theme }) =>
		isLast
			? 'none'
			: `1px solid ${theme.tokens.COLOR_TOKENS_STROKE_INVERTIBLE_NEUTRAL_DIVIDER_ULTRA_SUBTLE}`};

	${smallTableQuery} {
		height: 60px;
		padding: 8px 16px;
		min-width: 50px;
		gap: 8px;
		justify-content: ${({ isFeatureCell }) =>
			isFeatureCell ? 'flex-start' : 'center'};
	}
`;

export const StyledLogo = styled(Logo)`
	height: 30px;
`;

export const TextLogo = styled(Logo).attrs({
	variant: 'text',
})`
	height: 12px;
`;

export const CompetitorLogoText = styled.span`
	font-size: 24px;
	font-weight: 600;
	line-height: 25px;
	letter-spacing: 0;
	color: ${({ theme }) =>
		theme.tokens
			.COLOR_TOKENS_FILL_INVERTIBLE_NEUTRAL_TRANSPARENT_NEUTRAL_ULTRA_STRONG};

	${smallTableQuery} {
		text-align: center;
		font-size: 14px;
		line-height: 16px;
	}
`;

export const CompetitorLogoWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex: 1 0 0;

	${smallTableQuery} {
		gap: 8px;
		justify-content: center;
	}
`;
